import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthGuard } from "../App";
import { useNavigate } from "react-router-dom";
// Import FontAwesomeIcon and the specific icons
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faMoneyBillWave, faWallet, faMobileAlt, faReceipt, faInfoCircle, faSpinner, faQrcode } from "@fortawesome/free-solid-svg-icons"; // Added faQrcode for M-Pesa icon

const BillsPage = () => {
  const [bills, setBills] = useState(null); // Changed to null to differentiate between no bills and loading
  const [loading, setLoading] = useState(true);
  const [webUrl, setWebUrl] = useState("");
  const [showIframe, setShowIframe] = useState(false);
  const [isLoading, setIsLoading] = useState(false); // Used for individual payment processes
  const [balance, setBalance] = useState(0);
  const navigate = useNavigate();
  const [showMpesaModal, setShowMpesaModal] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [mpesaPaybillNumber, setMpesaPaybillNumber] = useState(null); // New state for M-Pesa Paybill number
  const [loadingPaybill, setLoadingPaybill] = useState(true); // New loading state for Paybill

  // Get user ID from localStorage
  const token = localStorage.getItem("token");
  const userDataString = localStorage.getItem("user");
  let userId = null;
  try {
    if (userDataString) {
      const userData = JSON.parse(userDataString);
      userId = userData.unique;
    }
  } catch (e) {
    console.error("Error parsing user data from localStorage:", e);
    // Handle parsing error, e.g., redirect to login or show an error message
    toast.error("User data unavailable. Please log in again.");
    navigate("/login"); // Example: redirect to login if user data is corrupted
  }

  // Effect to fetch user balance and bills
  useEffect(() => {
    const fetchBillsAndBalance = async () => {
      if (!userId || !token) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        // Fetch user balance
        const balanceResponse = await axios.get(
          `https://backend.inyumba.co.ke/iNyumba/balance/user/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setBalance(
          balanceResponse.data.response && balanceResponse.data.response.length > 0
            ? balanceResponse.data.response[0].balance
            : 0
        );

        // Fetch bills
        const billsResponse = await axios.get(
          `https://backend.inyumba.co.ke/iNyumba/admin/bill/view-bill/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (billsResponse.data.response.length === 0) {
          setBills({}); // Set an empty object to indicate no bills are due, but a structure exists
          toast.info("No active bills available at this time. 😊");
        } else {
          setBills(billsResponse.data.response);
        }
      } catch (error) {
        const errorMessage = error.response
          ? error.response.data.response
          : "An unexpected error occurred while fetching bills.";
        toast.error(errorMessage);
        setBills({}); // Ensure bills is an object even on error to avoid rendering issues
      } finally {
        setLoading(false);
      }
    };

    fetchBillsAndBalance();
  }, [userId, token, navigate]);

  // New useEffect to fetch M-Pesa Paybill number
  useEffect(() => {
    const fetchMpesaPaybill = async () => {
      if (!userId || !token) {
        setLoadingPaybill(false);
        return;
      }

      setLoadingPaybill(true);
      try {
        const response = await axios.get(
          `https://backend.inyumba.co.ke/iNyumba/view/mpesapaybillnumber/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        console.log(response);
        if (response.data && response.data.response) {
          setMpesaPaybillNumber(response.data.response);
        } else {
          toast.warn("M-Pesa Paybill number not found.");
          setMpesaPaybillNumber("N/A"); // Indicate not available
        }
      } catch (error) {
        console.error("Error fetching M-Pesa Paybill number:", error);
        toast.error("Failed to fetch M-Pesa Paybill number.");
        setMpesaPaybillNumber("N/A"); // Indicate not available on error
      } finally {
        setLoadingPaybill(false);
      }
    };
    fetchMpesaPaybill();
  }, [userId, token]);

  const handlePayments = async () => {
    setIsLoading(true);
    if (!userId || !token) {
      toast.warn("Authentication details missing. Please log in.");
      setIsLoading(false);
      return;
    }

    try {
      const response = await axios.post(
        `https://backend.inyumba.co.ke/iNyumba/user/payment/initiate-payment/${userId}`,
        null,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success === true) {
        setWebUrl(response.data.response);
        toast.success("Redirecting for payment... Please complete the process. 🚀");
        setShowIframe(true);
      } else {
        toast.error(response.data.response || "Payment initiation failed!");
      }
    } catch (error) {
      const errorMessage = error.response
        ? error.response.data.response
        : "An unexpected error occurred during payment initiation.";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMpesaPayments = async (phoneNumber) => {
    setIsLoading(true);
    if (!userId || !token) {
      toast.warn("Authentication details missing. Please log in.");
      setIsLoading(false);
      return;
    }

    try {
      const response = await axios.post(
        `https://backend.inyumba.co.ke/iNyumba/user/mpesapayment/rentpaymentinitiatestk/${userId}`,
        { phoneNumber },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success === true) {
        toast.info("Kindly input your M-Pesa PIN on the pop-up. 📱");
      } else {
        toast.error(response.data.response || "M-Pesa payment initiation failed!");
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.response ||
        "An error occurred with M-Pesa STK push. Please check your number or try again.";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseIframe = () => {
    setShowIframe(false);
    setWebUrl("");
    // Optionally re-fetch bills and balance to reflect payment
    // fetchBillsAndBalance(); // Uncomment if you want to refresh data after iframe close
    navigate("/user/myapartment");
  };

  const handlePayViaWallet = async () => {
    setIsLoading(true);
    if (!userId || !token) {
      toast.warn("Authentication details missing. Please log in.");
      setIsLoading(false);
      return;
    }

    try {
      const response = await axios.post(
        `https://backend.inyumba.co.ke/iNyumba/payviawallet/${userId}`,
        null,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        toast.success("Payment successful via wallet! 🎉");
        // Re-fetch bills and balance to update UI
        // This is a good place to call fetchBillsAndBalance() again to refresh data
        // For simplicity, uncommenting this would require making fetchBillsAndBalance a useCallback or similar if passed as prop
        // For now, let's just trigger a re-fetch of all data
        setLoading(true); // Manually trigger loading to refetch everything
        window.location.reload(); // Simple reload for demo, consider more sophisticated state management
      } else {
        toast.error(response.data.response);
      }
    } catch (error) {
      const errorMessage = error.response
        ? error.response.data.response
        : "An unexpected error occurred during wallet payment.";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseMpesaModal = () => {
    setShowMpesaModal(false);
    setPhoneNumber("");
  };

  const handleOkMpesaModal = () => {
    if (phoneNumber.trim() === "") {
      toast.error("Please enter a phone number. 📞");
      return;
    }
    handleMpesaPayments(phoneNumber);
    setShowMpesaModal(false);
    setPhoneNumber("");
  };

  return (
    <AuthGuard>
      <div className="min-h-screen bg-gray-100 p-4 sm:p-6 lg:p-8">

        <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-xl overflow-hidden">
          {/* Header Section */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-6 text-white text-center">
            <h1 className="text-3xl font-bold mb-2">Your Monthly Bills 🧾</h1>
            <p className="text-lg">Stay on top of your payments.</p>
          </div>

          <div className="p-6">
            {/* Current Balance */}
            <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-800 p-4 mb-6 rounded-md shadow-sm flex items-center justify-between">
              <p className="font-semibold text-lg flex items-center">
                <FontAwesomeIcon icon={faWallet} className="mr-3 text-2xl" />
                Current Wallet Balance: <span className="ml-2 text-xl font-bold text-yellow-700">Ksh {balance.toLocaleString()}</span>
              </p>
            </div>

            {/* Bill Details Section */}
            <div className="bg-white rounded-md p-6 border border-gray-200 mb-6 shadow-sm">
              <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
                <h2 className="text-2xl font-semibold text-gray-800">
                  <FontAwesomeIcon icon={faReceipt} className="mr-3 text-blue-600" />
                  Bill Overview
                </h2>
                <span className="text-md font-medium text-gray-500">
                  Date: {new Date().toLocaleDateString()}
                </span>
              </div>

              {loading ? (
                // Loading State UI
                <div className="text-center py-8">
                  <p className="text-gray-600 text-lg">Loading bills and payment options... ⏳</p>
                  <div className="animate-pulse flex space-x-4 mt-6">
                    <div className="flex-1 space-y-4 py-1">
                      <div className="h-4 bg-gray-200 rounded"></div>
                      <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                    </div>
                  </div>
                  {/* Buttons still appear here while loading, but iPay is disabled and M-Pesa controlled by isLoading */}
                  <div className="flex flex-col gap-4 mt-6">
                    <button
                      onClick={handlePayments}
                      className={`w-full py-3 px-6 rounded-lg text-lg font-semibold transition duration-300 ease-in-out flex items-center justify-center ${
                        isLoading
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-blue-600 text-white hover:bg-blue-700"
                      }`}
                      disabled={true} // iPay button is always disabled as per requirement
                    >
                      {isLoading ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Processing...
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faMoneyBillWave} className="mr-3" />
                          Pay Bills via iPay
                        </>
                      )}
                    </button>

                    <button
                      onClick={() => setShowMpesaModal(true)}
                      className={`w-full py-3 px-6 rounded-lg text-lg font-semibold transition duration-300 ease-in-out flex items-center justify-center ${
                        isLoading
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-green-600 text-white hover:bg-green-700"
                      }`}
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Processing...
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faMobileAlt} className="mr-3" />
                          Pay Bills via M-Pesa
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                // Content when loading is false
                <>
                  {bills && Object.keys(bills).length === 0 ? (
                    // No Bills Available State
                    <div className="text-center py-8">
                      <p className="text-gray-600 text-lg mb-4">You currently have no active bills. Enjoy! 🎉</p>
                      {/* Removed the static image here */}
                      <div className="flex flex-col gap-4 mt-6"> {/* Buttons also here */}
                        <button
                          onClick={handlePayments}
                          className={`w-full py-3 px-6 rounded-lg text-lg font-semibold transition duration-300 ease-in-out flex items-center justify-center ${
                            isLoading
                              ? "bg-gray-400 cursor-not-allowed"
                              : "bg-blue-600 text-white hover:bg-blue-700"
                          }`}
                          disabled={true} // iPay button is always disabled as per requirement
                        >
                          {isLoading ? (
                            <>
                              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Processing...
                            </>
                          ) : (
                            <>
                              <FontAwesomeIcon icon={faMoneyBillWave} className="mr-3" />
                              Pay for Deposit via iPay
                            </>
                          )}
                        </button>

                        <button
                          onClick={() => setShowMpesaModal(true)}
                          className={`w-full py-3 px-6 rounded-lg text-lg font-semibold transition duration-300 ease-in-out flex items-center justify-center ${
                            isLoading
                              ? "bg-gray-400 cursor-not-allowed"
                              : "bg-green-600 text-white hover:bg-green-700"
                          }`}
                          disabled={isLoading}
                        >
                          {isLoading ? (
                            <>
                              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Processing...
                            </>
                          ) : (
                            <>
                              <FontAwesomeIcon icon={faMobileAlt} className="mr-3" />
                              Pay Deposit via M-Pesa
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  ) : (
                    // Bills Present State
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-6 mb-8">
                        {/* Bill Items */}
                        <BillDetail label="Water Bill" amount={bills.WaterBill} />
                        <BillDetail label="Rent Bill" amount={bills.RentBill} />
                        <BillDetail label="Trash Bill" amount={bills.Trashbill} />
                        <BillDetail label="Total Amount Due" amount={bills.TotalAmount} highlight />
                      </div>

                      <div className="flex flex-col gap-4">
                        {/* Payment Buttons */}
                        <button
                          onClick={handlePayments}
                          className={`w-full py-3 px-6 rounded-lg text-lg font-semibold transition duration-300 ease-in-out flex items-center justify-center ${
                            isLoading
                              ? "bg-gray-400 cursor-not-allowed"
                              : "bg-blue-600 text-white hover:bg-blue-700"
                          }`}
                          disabled={true} // iPay button is always disabled as per requirement
                        >
                          {isLoading ? (
                            <>
                              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Processing...
                            </>
                          ) : (
                            <>
                              <FontAwesomeIcon icon={faMoneyBillWave} className="mr-3" />
                              Pay Bills via iPay
                            </>
                          )}
                        </button>

                        <button
                          onClick={() => setShowMpesaModal(true)}
                          className={`w-full py-3 px-6 rounded-lg text-lg font-semibold transition duration-300 ease-in-out flex items-center justify-center ${
                            isLoading
                              ? "bg-gray-400 cursor-not-allowed"
                              : "bg-green-600 text-white hover:bg-green-700"
                          }`}
                          disabled={isLoading}
                        >
                          {isLoading ? (
                            <>
                              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Processing...
                            </>
                          ) : (
                            <>
                              <FontAwesomeIcon icon={faMobileAlt} className="mr-3" />
                              Pay Bills via M-Pesa
                            </>
                          )}
                        </button>

                        {balance >= bills.TotalAmount && (
                          <button
                            onClick={handlePayViaWallet}
                            className={`w-full py-3 px-6 rounded-lg text-lg font-semibold transition duration-300 ease-in-out flex items-center justify-center ${
                              isLoading
                                ? "bg-gray-400 cursor-not-allowed"
                                : "bg-purple-600 text-white hover:bg-purple-700"
                            }`}
                            disabled={isLoading}
                          >
                            {isLoading ? (
                              <>
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Processing...
                              </>
                            ) : (
                              <>
                                <FontAwesomeIcon icon={faWallet} className="mr-3" />
                                Pay via Wallet (Ksh {balance.toLocaleString()})
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </>
              )}

              <p className="text-sm text-gray-500 mt-6 text-center italic">
                <span className="font-semibold">Note:</span> If you experience issues with payment prompts, please use the manual payment instructions.
              </p>
            </div>

            {/* Manual Payment Instructions - Dynamic M-Pesa Section */}
            <div className="bg-white rounded-md p-6 border border-gray-200 shadow-sm">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4 pb-4 border-b border-gray-200 flex items-center">
                <FontAwesomeIcon icon={faQrcode} className="mr-3 text-green-600" />
                Manual M-Pesa Payment Instructions
              </h2>
              {loadingPaybill ? (
                <div className="text-center py-4">
                  <FontAwesomeIcon icon={faSpinner} spin className="text-green-500 text-3xl mb-2" />
                  <p className="text-gray-600">Fetching M-Pesa Paybill details...</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <p className="text-lg font-semibold text-green-800 flex items-center">
                      <FontAwesomeIcon icon={faInfoCircle} className="mr-2" />
                      Follow these steps on your M-Pesa menu:
                    </p>
                    <ol className="list-decimal list-inside text-gray-700 mt-2 space-y-1">
                      <li>Go to **M-Pesa** on your phone.</li>
                      <li>Select **Lipa Na M-Pesa**.</li>
                      <li>Select **Pay Bill**.</li>
                      <li>Enter Business Number: <span className="font-bold text-green-900">{mpesaPaybillNumber}</span></li>
                      <li>Enter Account No.: <span className="font-bold text-green-900">Your Phone Number</span></li>
                      <li>Enter the **Amount** to pay.</li>
                      <li>Enter your **M-Pesa PIN** and confirm.</li>
                    </ol>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Full-screen Iframe Modal */}
        {showIframe && webUrl && (
          <div className="fixed inset-0 bg-gray-900 bg-opacity-90 flex flex-col items-center justify-center z-50 p-4">
            <button
              onClick={handleCloseIframe}
              className="absolute top-4 right-4 bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-full text-lg shadow-lg transition-transform transform hover:scale-105"
            >
              <FontAwesomeIcon icon={faTimes} className="mr-2" />
              Close Payment
            </button>
            <iframe
              src={webUrl}
              className="w-full h-full max-w-7xl max-h-[90vh] rounded-lg shadow-2xl border-none"
              title="Payment Gateway"
              allowFullScreen
            ></iframe>
          </div>
        )}

        {/* M-Pesa Phone Number Input Modal */}
        {showMpesaModal && (
          <div className="fixed inset-0 bg-gray-900 bg-opacity-70 flex items-center justify-center z-50">
            <div className="bg-white p-8 rounded-xl shadow-2xl w-full max-w-md mx-4 transform transition-all duration-300 scale-100 animate-fade-in">
              <div className="flex justify-between items-center mb-6 border-b pb-4">
                <h3 className="text-2xl font-bold text-gray-800">Enter M-Pesa Phone Number</h3>
                <button
                  onClick={handleCloseMpesaModal}
                  className="text-gray-500 hover:text-red-600 transition duration-200"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-3xl" />
                </button>
              </div>

              <div className="mb-6">
                <label htmlFor="phoneNumber" className="block text-gray-700 text-sm font-semibold mb-2">
                  Phone Number:
                </label>
                <input
                  id="phoneNumber"
                  type="tel" // Use type="tel" for phone numbers
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-lg"
                  placeholder="e.g., 0712345678"
                  aria-label="Phone number for M-Pesa payment"
                />
              </div>

              <div className="flex justify-end gap-4">
                <button
                  onClick={handleCloseMpesaModal}
                  className="bg-gray-200 text-gray-800 px-6 py-3 rounded-lg font-semibold hover:bg-gray-300 transition duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleOkMpesaModal}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition duration-200 flex items-center"
                >
                  <FontAwesomeIcon icon={faMobileAlt} className="mr-2" />
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AuthGuard>
  );
};

// Helper component for displaying bill details
const BillDetail = ({ label, amount, highlight }) => (
  <div className={`flex flex-col p-3 rounded-md ${highlight ? 'bg-blue-50 border-blue-200 border' : 'bg-gray-50 border-gray-200 border'}`}>
    <span className="text-sm font-medium text-gray-600">{label}:</span>
    <span className={`text-xl font-bold ${highlight ? 'text-blue-700' : 'text-gray-900'}`}>
      Ksh {amount ? amount.toLocaleString() : 'N/A'}
    </span>
  </div>
);

export default BillsPage;